import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Skeleton } from "@/components/ui/skeleton";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return ""; }
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/blog/posts?limit=100").then((r) => setPosts(r.data.posts || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(posts.map((p) => p.category));
    return ["All", ...Array.from(set).sort()];
  }, [posts]);

  const filtered = useMemo(() => {
    if (filter === "All") return posts;
    return posts.filter((p) => p.category === filter);
  }, [posts, filter]);

  return (
    <>
      <Seo
        title="Eye Health Blog — Nehchal Eye Centre Noida"
        description="Expert eye health articles from Dr. Gurkiran Kaur — cataract, glaucoma, oculoplasty, pediatric, dry eye and diabetic retinopathy insights for Noida patients."
        path="/blog"
      />
      <Breadcrumbs items={[{ name: "Blog", path: "/blog" }]} />

      <section className="container-wide pt-10 pb-8">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-widest text-accent mb-3">Eye Health Insights</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05]">
            Practical eye care from Dr. Gurkiran Kaur.
          </h1>
          <p className="mt-6 text-foreground/70 leading-7">
            Guides on cataract, glaucoma, oculoplasty and everyday eye health — written for patients in Noida and Delhi NCR.
          </p>
        </div>
      </section>

      {!loading && categories.length > 2 && (
        <section className="container-wide pb-6">
          <div className="flex flex-wrap gap-2" data-testid="blog-category-filters">
            {categories.map((c) => {
              const on = filter === c;
              return (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className={`inline-flex items-center rounded-full border px-4 py-1.5 text-sm transition-colors ${
                    on
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-foreground/70 border-border hover:border-primary/40"
                  }`}
                  data-testid={`blog-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section className="container-wide pb-24">
        {loading ? (
          <div className="grid md:grid-cols-2 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-72 w-full rounded-xl" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground">No articles in this category yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-6" data-testid="blog-list">
            {filtered.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="group rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all overflow-hidden flex flex-col"
                data-testid={`blog-card-${p.slug}`}
              >
                {p.cover_image ? (
                  <div className="aspect-[16/9] w-full bg-secondary overflow-hidden">
                    <img
                      src={p.cover_image}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                ) : null}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                    <span className="rounded-full bg-primary/10 text-primary px-2.5 py-0.5">{p.category}</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> {formatDate(p.created_at)}
                    </span>
                  </div>
                  <h2 className="font-serif text-xl sm:text-2xl text-foreground group-hover:text-primary transition-colors">
                    {p.title}
                  </h2>
                  <p className="mt-3 text-sm text-foreground/70 leading-6 line-clamp-3 flex-1">{p.excerpt}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm text-primary">
                    Read article <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
